import { SharePointParentFolder } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import { useCreateDesignSubmittalMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleCreateNotification } from 'hooks/notificationHandler'
import AdvancedCreateModal from 'components/CIMSModal/AdvancedCreateModal'
import DetailsBody from './DetailsBody'
import AdditionalInfoBody from './AdditionalInfoBody'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { ExternalLinkEditorValues } from 'components/ExternalLinkEditor/types'
import { DetailsBodyValues } from './DetailsBody/types'
import { AdditionalInfoBodyValues } from './AdditionalInfoBody/types'
import { useDesignSubmittalLogContext } from '../../../../../../DesignSubmittalLogContext'
import { errorHandler } from 'hooks/errorHandler'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'

export type CreateDesignSubmittalModalData = DetailsBodyValues &
  AdditionalInfoBodyValues &
  ExternalLinkEditorValues

export const CreateDesignSubmittalModal: React.FC<
  CIMSModalWrapperProps<Partial<CreateDesignSubmittalModalData>>
> = ({ visible, onClose, state }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    metaFetching,
    selectOptions: {
      actionsOptions,
      typesOptions,
      orgsOptions,
      disciplinesOptions,
      fdcOptions,
    },
    quickLinks,
  } = useDesignSubmittalLogContext()
  const { toMutateCustomFieldsInput } = useProjectCustomFieldsContext()

  const [createDesignSubmittal] = useCreateDesignSubmittalMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createDesignSubmittal: {
        designSubmittal: { sharePointFolderName },
      },
    }) => simpleCreateNotification(sharePointFolderName),
  })

  const { fromLabeledValues } = usePayItemSelectContext()
  return (
    <AdvancedCreateModal
      initialFormValues={{ fdcIds: state?.fdcIds }}
      visible={visible}
      onClose={onClose}
      details={
        <DetailsBody orgsOptions={orgsOptions} typesOptions={typesOptions} />
      }
      additionalInfo={
        <AdditionalInfoBody
          actionsOptions={actionsOptions}
          disciplinesOptions={disciplinesOptions}
          fdcOptions={fdcOptions}
        />
      }
      attachments={{
        sharePoint: {
          parent: SharePointParentFolder.DesignSubmittals,
          folder: ({ packageNumber, packageName }) =>
            `${packageNumber}-${packageName}`,
        },
        externalLinks: {
          quickLinks: quickLinks,
          loading: metaFetching,
        },
      }}
      ok={{
        fn: async (store) => {
          const values = store as CreateDesignSubmittalModalData
          const {
            requestedActionDate,
            disciplineIds,
            payItemIds,
            fdcIds,
            customFields,
            ...rest
          } = values

          if (!projectId) return errorHandler('Project id must be defined')

          await createDesignSubmittal({
            variables: {
              input: {
                projectId,
                requestedActionDate: requestedActionDate.toISOString(),
                disciplineIds,
                payItemIds: fromLabeledValues(payItemIds),
                fieldDesignChangeIds: fdcIds?.map(({ value }) =>
                  value.toString()
                ),
                customFields: toMutateCustomFieldsInput(customFields),
                ...rest,
              },
            },
          })
        },
      }}
    />
  )
}
